import { Component, EventEmitter, OnDestroy, OnInit } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { isValidDate } from "@app/_helpers/date-fns";
import formatByLocale from "@app/_helpers/date-fns/format";
import { hasPermissionByKey, isAdmin } from "@app/_helpers/permission";
import { uniqRulebreakTypeDateFn, uniqRulebreakTypeFn } from "@app/_helpers/rulebreaks/utils";
import {
	createDateArrayWithRange,
	createDateRangeArray,
	createRxValue,
	createScheduleDayParserFromUser,
	DateRangeType,
	distinctUntilChangedJson,
	fromRxValue,
	getSeriesTypeDateBounds,
	isNullOrUndefined,
	parseScheduleStats,
} from "@app/_helpers/utils";
import { UtilService } from "@app/_services/util.service";
import { AppService } from "@app/app.service";
import { SuperviseBreakTime } from "@app/pages/supervisor-page/supervise-user-tab/supervise-user-activity/supervise-user-activity.component";
import { disabledFlags } from "@app/shared/time-comego-view/time-comego-view.component";
import { TIME_RANGE_CUSTOM_NAME, TRange } from "@app/shared/time-range-picker/time-range-constants";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	ComegoQuery,
	ComegoService,
	ComegoTime,
	Logger,
	Time,
	UserSchedule,
	UserSettings,
	UserSettingsQuery,
} from "@timeghost/web-api";
import { ApexOptions } from "apexcharts";
import { addDays, endOfDay, isSameDay, startOfDay, startOfMinute, startOfToday } from "date-fns/esm";
import { merge } from "lodash-es";
import {
	combineLatest,
	distinctUntilChanged,
	map,
	OperatorFunction,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from "rxjs";
import { firstBy } from "thenby";
import { canManageSchedulesOthers, ReportsService } from "../reports.service";
import { aggregateSchedulesGraphSeries, computeUserScheduleCaps } from "../reports-schedules-graph-series";
import { ReportItem } from "../utils";

const log = new Logger("SchedulesPageComponent");

type ScheduleTableItem = {
	enabled: boolean;
	summed: number;
	overtime: number;
	missing: number;
	schedules: ReturnType<typeof parseScheduleStats>[];
};
type ScheduleTable = Record<string, ScheduleTableItem>;

@UntilDestroy()
@Component({
	selector: "tg-schedules-page",
	templateUrl: "./schedules-page.component.html",
	styleUrls: ["./schedules-page.component.scss"],
})
export class SchedulesPageComponent implements OnInit, OnDestroy {
	constructor(
		private reports: ReportsService,
		private userSettingsQuery: UserSettingsQuery,
		private translate: TranslateService,
		private appService: AppService,
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery
	) {}

	readonly statsDifferenceExpand$ = createRxValue<boolean>(true);
	readonly statsAbsencesExpand$ = createRxValue<boolean>(true);
	readonly workspaceScheduleEnabled = this.userSettingsQuery.select((x) => x.workspace.schedules.workspace.enabled);
	readonly legacyDate$ = this.reports.legacyDate$;
	readonly beforeLegacyDate$ = fromRxValue(
		this.legacyDate$.value$.pipe(
			untilDestroyed(this),
			map((date) => (date ? addDays(date, -1) : null))
		)
	);

	readonly legacyInfoDisplay$ = createRxValue<"none" | "rangeBefore" | "rangeWithin">("none");

	ngOnInit(): void {
		const user = this.userSettingsQuery.getValue();
		const canManageOthers = canManageSchedulesOthers(user);
		if (!canManageOthers) {
			this.reports.teamFilter.update([user.id]);
			this.reports.groupFilter.update([]);
		}
		log.debug("ngOnInit", user.id, { canManageOthers });

		this.range$.pipe(untilDestroyed(this)).subscribe((range) => {
			const legacyDate = this.legacyDate$.value;
			const allHoursEnabled = this.userSettingsQuery.getValue().workspace?.settings.allHoursEnabled;
			this.legacyInfoDisplay$.update("none");
			if (!allHoursEnabled) return;
			if (!legacyDate) return;
			if (legacyDate < range.from) {
				this.legacyInfoDisplay$.update("none");
			} else if (range.to < legacyDate) {
				this.legacyInfoDisplay$.update("rangeBefore");
			} else if (range.to > legacyDate) {
				this.legacyInfoDisplay$.update("rangeWithin");
			}
		});
	}

	ngOnDestroy() {
		this.beforeLegacyDate$.unsubscribe();
		this.auditRulebreaks.unsubscribe();
		this.data$graph.unsubscribe();
	}

	get range() {
		return this.reports.range.value;
	}
	set range(val: TRange) {
		this.drillHistory$.update([]);
		this.reports.onRangeUpdate(val);
	}
	readonly timeLoading$ = this.reports.timeLoading$;
	readonly comegoEnabled$ = this.appService.comegoEnabled$;

	readonly drillHistory$ = createRxValue<TRange[]>([]);
	readonly canDrillBack$ = this.drillHistory$.value$.pipe(map((h) => h.length > 0));

	drillBack() {
		const history = this.drillHistory$.value;
		if (!history.length) return;
		const prev = history[history.length - 1];
		this.drillHistory$.update(history.slice(0, -1));
		this.reports.onRangeUpdate(prev);
	}

	private _drillRange(val: TRange) {
		this.drillHistory$.update([...this.drillHistory$.value, this.range]);
		this.reports.onRangeUpdate(val);
	}

	readonly graphDrillable$ = this.reports.range$.pipe(
		map((range) => {
			const [, seriesType] = createDateRangeArray(range.from, range.to, true);
			return seriesType === "week" || seriesType === "month";
		}),
		distinctUntilChanged(),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	private buildDrillClickHandler(seriesType: DateRangeType, seriesRange: Array<[Date, any, any]>) {
		return (_event: MouseEvent, _chartContext: unknown, config: { dataPointIndex?: number }) => {
			if (seriesType !== "week" && seriesType !== "month") return;
			const index = config?.dataPointIndex;
			if (index == null || index < 0) return;
			const date = seriesRange[index]?.[0];
			if (!date) return;
			const bounds = getSeriesTypeDateBounds(seriesType, date);
			if (!bounds) return;
			// clamp to the outer range so a partial first/last week stays within the selected month
			const outerRange = this.reports.range.value;
			const from = bounds.from < outerRange.from ? outerRange.from : bounds.from;
			const to = bounds.to > outerRange.to ? outerRange.to : bounds.to;
			this._drillRange({ name: TIME_RANGE_CUSTOM_NAME, rangeType: null, from, to });
		};
	}
	private mapDataToComego<T>(): OperatorFunction<
		[ReportItem<ComegoTime>[], UserSettings, ...T[]],
		[ReportItem<ComegoTime>[], UserSettings, ...T[]]
	> {
		return map(([_times, user, ...args]: [ReportItem<ComegoTime>[], UserSettings, ...any[]]) => {
			return [
				user.workspace.settings?.workinghoursEnabled
					? _times.filter((x: any) => x.type && !disabledFlags.includes(x.type.name))
					: _times,
				user,
				...args,
			];
		});
	}
	private refresh$ = new EventEmitter(true);
	refresh() {
		this.refresh$.next(Date.now());
	}
	readonly comego$ = combineLatest([
		this.userSettingsQuery
			.select((x) => x.workspace)
			.pipe(
				map((x) => x.settings),
				startWith(this.userSettingsQuery.getValue()?.workspace?.settings),
				distinctUntilChangedJson()
			),
		this.reports.range$,
		this.reports.data$,
		this.refresh$.asObservable().pipe(
			startWith(Date.now()),
			map(() => Date.now())
		),
	]).pipe(
		switchMap(async ([settings, range, data]) => {
			return data.comego;
		})
	);
	readonly data$table = combineLatest([
		this.comego$,
		this.reports.roundingData$,
		this.userSettingsQuery.select(),
	]).pipe(
		this.reports.roundingHandler(false),
		this.mapDataToComego(),
		tap(() => log.debug("Data$Table"))
	);
	readonly auditRulebreaks = fromRxValue(
		this.reports.auditFilter.asObservable().pipe(
			untilDestroyed(this),
			map((x) => x?.rulebreaks === true)
		)
	);
	readonly data$audit = combineLatest([this.data$table, this.reports.auditFilter.asObservable()]).pipe(
		map(([data, audit]) => {
			const [times, user, ...args] = data;
			if (audit.rulebreaks) {
				return [
					(times as any as SuperviseBreakTime["times"]).filter((d) => {
						return d?.meta && d.meta?.errors?.length;
					}),
					user,
					...args,
				] as any as typeof data;
			}
			return data;
		})
	);
	readonly showAmounts$ = this.reports.showAmounts$;
	readonly users$ = combineLatest([this.reports.users$, this.userSettingsQuery.select()]).pipe(
		map(([users, user]) => {
			return users.filter((x) => user.workspace.users?.find((y) => y.id === x));
		})
	);
	readonly range$ = this.reports.range$;
	readonly data$ = combineLatest([this.data$table, this.users$, this.range$, this.comego$]).pipe(
		distinctUntilChangedJson(),
		map(([[_times], users, range, comegoTimes]) => {
			const user = this.userSettingsQuery.getValue();
			type DataReturn = {
				id: string;
				summed: number;
				missing: number;
				overtime: number;
				future: number;
				absence: number;
				times: Time[];
				cap: number;
				difference: number;
				editable: boolean;
			};
			const startOfPending = startOfToday();
			const roundFn = (ms: number | Date) => startOfMinute(new Date(ms));
			const userTimeMap = users.reduce(
				(acc, uid) => {
					if (acc[uid]) return acc;
					let utimes = _times.filter((x) => x.userId === uid);
					const employment_date = ((d) => {
						let dt = new Date(d);
						if (d && isValidDate(dt)) return startOfDay(dt);
						return null;
					})(user.workspace.users.find((d) => d.id === uid)?.["employment_date"]);
					const { summed, cap, future, missing, overtime, difference, absence, schedules } =
						createDateRangeArray(range.from, range.to)[0].reduce(
							(dateRangeSum, [currentRangeDate]) => {
								if (employment_date && currentRangeDate < employment_date) return dateRangeSum;
								const dayScheduleParser = createScheduleDayParserFromUser(
									{ ...user, id: uid },
									currentRangeDate,
									{ round: roundFn },
									true
								);
								if (!dayScheduleParser) return dateRangeSum;
								const worktime = utimes.filter((a) => !a?.type || a?.type?.name != "pause");
								const daySchedule = dayScheduleParser(
									worktime.map(
										(a) =>
											({
												start: a.start,
												end: a.end,
												timeDiff: a.duration,
												type: a?.type?.name ?? "work",
											}) as unknown as Time
									),
									currentRangeDate
								);
								// absence
								let absenceDurationDay;
								if (user.workspace.settings?.workinghoursEnabled) {
									const absence = utimes.filter((time: any) => time?.type?.name == "absence");
									absenceDurationDay = dayScheduleParser(
										absence.map(
											(a) =>
												({
													start: a.start,
													end: a.end,
													timeDiff: a.duration,
													type: a?.type?.name,
												}) as unknown as Time
										),
										currentRangeDate
									).usage;
									dateRangeSum.absence += absenceDurationDay;
								}
								//summed
								if (daySchedule.enabled) dateRangeSum.cap += ~~daySchedule.cap;
								dateRangeSum.summed += ~~daySchedule.usage;
								// difference
								const diffMs = ~~daySchedule.cap - ~~daySchedule.usage;
								const absenceLargerThenCap = absenceDurationDay > daySchedule.cap;
								if (!absenceLargerThenCap)
									dateRangeSum.difference += Math.floor(
										currentRangeDate < startOfPending ? -diffMs : -diffMs > 0 ? -diffMs : 0
									);
								// missing or overtime
								if (absenceLargerThenCap) {
									dateRangeSum.overtime += ~~daySchedule.usage - absenceDurationDay;
									dateRangeSum.overtime += absenceDurationDay - daySchedule.cap;
									dateRangeSum.difference += ~~daySchedule.usage - absenceDurationDay;
									dateRangeSum.difference += absenceDurationDay - daySchedule.cap;
								} else if (diffMs < 0) dateRangeSum.overtime += -diffMs;
								else if (diffMs >= 0) dateRangeSum.missing += -diffMs;
								// future
								if (currentRangeDate >= startOfPending) {
									const futureMs = daySchedule.enabled
										? (~~daySchedule.cap - ~~daySchedule.usage).clampThis(0.0)
										: Math.floor(daySchedule.usage);
									dateRangeSum.future += futureMs;
								}

								if (!dateRangeSum.schedules.find((x) => x === daySchedule.schedule))
									dateRangeSum.schedules.push(daySchedule.schedule as any);
								return dateRangeSum;
							},
							{
								summed: 0,
								missing: 0,
								overtime: 0,
								cap: 0,
								future: 0,
								difference: 0,
								absence: 0,
								schedules: new Array<UserSchedule>(),
							}
						);
					const newParsedUserTimes = comegoTimes
						.filter((x) => x.userId === uid)
						.map((t) => {
							const { start, end, ...ut } = t;
							const newStart = startOfMinute(new Date(start));

							if (employment_date && newStart < employment_date) return null;
							const newEnd = startOfMinute(new Date(end || Date.now()));
							const editable =
								ut.userId && ut.userId !== user.id
									? hasPermissionByKey(user, "groupsCanComegoViewTimes" as any)
									: hasPermissionByKey(user, "groupsCanComegoUpdateTimes" as any);

							return {
								...ut,
								start: newStart,
								end: newEnd,
								editable,
								isWorkingDay: true,
							} as ReportItem<ComegoTime> & { editable: boolean; isWorkingDay: boolean };
						})
						.filter(Boolean);
					const currentSelectedUser = user.workspace.users?.find((d) => d.id === uid);
					const errors = newParsedUserTimes.flatMap(
						(d) => d.violations?.map((v) => ({ ...v, date: d.start.toISOString() })) ?? []
					);
					const errorsPerTime = errors.uniqBy(uniqRulebreakTypeDateFn);
					const errorTypeFiltered = errors.uniqBy(uniqRulebreakTypeFn);
					const errorTypeCount = errors.reduce(
						(bcc, e) => {
							bcc[e.type]++;
							return bcc;
						},
						{ break: 0, rest: 0, maxHours: 0 }
					);
					log.debug(uid, currentSelectedUser.email, { errorsPerTime, errorTypeFiltered, errorTypeCount });
					acc[uid] = {
						id: uid,
						summed,
						missing,
						overtime,
						future,
						absence,
						errorCount: errors.length,
						errorTypeCount,
						errors: errorTypeFiltered,
						times: newParsedUserTimes.sort(
							firstBy(({ start }: { start: Date }) => start, "desc").thenBy((x) => x.end, "asc")
						) as any,
						cap,
						recording: newParsedUserTimes.findIndex((d) => d["recording"]) !== -1,
						difference: Math.floor(difference / 60) * 60,
						editable: isAdmin(user),
						user: currentSelectedUser,
					} as any;
					return acc;
				},
				{} as { [key: string]: DataReturn }
			);
			return userTimeMap;
		})
	);
	readonly data$stats = combineLatest([this.data$, this.users$]).pipe(
		map(([data, users]) => {
			const workinghoursEnabled = this.userSettingsQuery.getValue().workspace.settings["workinghoursEnabled"];
			const { should, summed, workTime, difference, overtime, missing, absence, future } = Object.values(
				data
			).reduce(
				(acc, r) => {
					acc.should += r.cap;
					acc.future += r.future;
					acc.summed += r.summed;
					acc.workTime += r.summed - r.absence;
					acc.difference += r.difference;
					acc.overtime += r.overtime;
					acc.missing += r.missing + r.future;
					acc.absence += workinghoursEnabled ? r.absence : 0;
					return acc;
				},
				{ summed: 0, overtime: 0, should: 0, missing: 0, future: 0, difference: 0, absence: 0, workTime: 0 }
			);
			return {
				summed,
				should,
				difference,
				overtime,
				missing,
				future,
				workTime,
				absence,
			};
		})
	);
	readonly absencesSubcategories$stats = combineLatest([
		this.data$table,
		this.users$,
		this.range$,
		this.comego$,
	]).pipe(
		distinctUntilChangedJson(),
		map(([[_times], users, range, comegoTimes]) => {
			const user = this.userSettingsQuery.getValue();
			const absences = user?.workspace["workinghours"]?.["absenceSubcategories"] || [];
			_times = _times.filter((t: any) => t?.type?.name == "absence");

			let absencesValues: { id: string; name: string; value: number }[] = [];
			absencesValues = _times.reduce((acc, t: any) => {
				let id = t.type.id ?? "defaultAbsences";

				let index = acc.findIndex((a) => a.id == id);

				if (index === -1) {
					let absence = { title: "defaultAbsences" };
					if (id != "defaultAbsences") {
						absence = absences.find((absence: any) => absence.id === id);
						if (!absence) return acc;
					}
					acc.push({ id: id, name: absence.title, value: 0 });
					index = acc.length - 1;
				}

				acc[index].value += t.duration;

				return acc;
			}, []);
			return absencesValues;
		})
	);
	readonly data$statsRulebreaks = combineLatest([this.data$, this.users$]).pipe(
		map(([data]) => {
			const { breaks, dailyMax, breakBetweenDays } = Object.values(data).reduce(
				(acc, r: any) => {
					if (!r.errorTypeCount) return acc;
					const { break: breaks, rest, maxHours } = r.errorTypeCount;
					acc.breaks += breaks;
					acc.breakBetweenDays += rest;
					acc.dailyMax += maxHours;
					return acc;
				},
				{ breaks: 0, dailyMax: 0, breakBetweenDays: 0 }
			);
			return {
				breaks,
				dailyMax,
				breakBetweenDays,
				errors: breaks + dailyMax + breakBetweenDays,
			};
		})
	);
	readonly data$Visible = combineLatest(this.reports.comegoTypeFilter.asObservable()).pipe(
		map(([filter]) => {
			return {
				filterActive: !filter.length,
				showAbsence: !!filter.find((u) => u.length > 6) || !filter.length,
				showWork: filter.includes("work") || !filter.length,
			};
		})
	);
	readonly data$graph = fromRxValue(
		combineLatest([
			this.comego$,
			this.reports.roundingData$,
			this.userSettingsQuery.select(),
			this.users$,
			this.reports.range$,
			this.reports.comegoTypeFilter.asObservable(),
		]).pipe(
			untilDestroyed(this),
			tap((...args) => log.debug("data$graph hit", args)),
			this.reports.roundingHandler(false),
			this.mapDataToComego(),
			tap((...args) => log.debug("data$graph hit after rounding & mapDataToComego", args)),
			map(
				([_times, userSettings, users, range]: [
					ReportItem<ComegoTime>[],
					UserSettings,
					string[],
					TRange,
				]): ApexOptions => {
					const [seriesRange, seriesType] = createDateArrayWithRange<number>(
						startOfDay(range.from),
						endOfDay(range.to),
						true
					);

					const startOfPending = startOfToday();
					const userScheduleCap = computeUserScheduleCaps(users, userSettings, range.from, range.to);
					const data = aggregateSchedulesGraphSeries({
						times: _times,
						users,
						userSettings,
						seriesRange,
						seriesType,
						userScheduleCap,
						startOfPending,
					});
					const defaultGroup = "sum";
					const formatYAxis = (v: number, opt: any): any => {
						if (v === 0.0 || isNullOrUndefined(v)) return undefined;
						const yFormat = UtilService.parseMS(v * 1000, { showNegative: false, showSeconds: false });
						return yFormat;
					};
					let seriesData = [
						{
							data: data.absence,
							name: `${this.translate.instant("schedules.dashboard.absence")}`,
							color: "#9356e3",
							group: defaultGroup,
						},
					];
					if (this.reports.comegoTypeFilter.value.includes("work")) {
						const restData = [
							{
								data: data.usage,
								name: `${this.translate.instant("dashboard.schedules.ist")}`,
								color: "#FBBA00",
								group: defaultGroup,
							},
							{
								data: data.overtime,
								name: `${this.translate.instant("schedules.dashboard.overtime")}`,
								color: "#0C8875",
								group: defaultGroup,
							},
						];
						seriesData = [...seriesData, ...restData];
					}
					if (!this.reports.comegoTypeFilter.value.length) {
						const restData = [
							{
								data: data.usage,
								name: `${this.translate.instant("dashboard.schedules.ist")}`,
								color: "#FBBA00",
								group: defaultGroup,
							},
							{
								data: data.overtime,
								name: `${this.translate.instant("schedules.dashboard.overtime")}`,
								color: "#0C8875",
								group: defaultGroup,
							},
							{
								data: data.missing,
								name: `${this.translate.instant("dashboard.schedules.soll")}`,
								color: "#FF594B",
								group: defaultGroup,
							},
							{
								data: data.future,
								name: `${this.translate.instant("schedules.dashboard.future")}`,
								color: "#619CD7",
								group: defaultGroup,
							},
						];
						seriesData = [...seriesData, ...restData];
					}
					const missingSeriesName = this.translate.instant("dashboard.schedules.soll");
					const overTimeSeriesName = this.translate.instant("schedules.dashboard.overtime");
					const options = <Partial<ApexOptions>>{
						chart: {
							type: "bar",
							stackType: "normal",
							stacked: true,
							events: {
								click: this.buildDrillClickHandler(seriesType, seriesRange),
							},
						},
						series: seriesData,
						xaxis: {
							categories: seriesRange.map(([key]) => key),
						},

						plotOptions: {
							bar: {
								horizontal: false,
								dataLabels: {
									total: {
										enabled: true,
										formatter: function (_value, opts) {
											const series = opts.config.series;
											const index = opts.dataPointIndex;
											const duration = series.reduce(
												(sum: number, s: { data?: number[]; name?: string }) => {
													const value = s?.data?.[index] ?? 0;
													if (
														s?.name !== overTimeSeriesName &&
														s?.name !== missingSeriesName
													) {
														return sum;
													}
													return s?.name === missingSeriesName ? sum - value : sum + value;
												},
												0
											);
											const formattedWorktime = formatYAxis(Math.abs(duration), null);
											if (!formattedWorktime) return "+/- 00:00";
											const prefix = duration > 0 ? "+ " : duration < 0 ? "- " : "";
											return prefix + formattedWorktime.toString();
										},
										offsetX: 0,
										offsetY: -10,
										style: {
											fontSize: "0.875rem",
											fontWeight: 700,
											color: "hsl(var(--secondary))",
											fontFamily: "inherit",
										},
									},
								},
							},
						},
					};
					return merge(options, <Partial<ApexOptions>>{
						yaxis: {
							labels: {
								formatter: (v, opts) => {
									// // percentage step check
									// if (typeof opts === 'number') {
									//   const percentage = v;
									//   const series = options.series as Array<{ data: number[] }>;
									//   const newValue = series!
									//     .reduce((pcc, p) => {
									//       p.data.forEach((d, idx) => {
									//         pcc[idx] += d;
									//       });
									//       return pcc;
									//     }, new Array<number>(series[0]!.data!.length).fill(0))
									//     .sort(firstBy((d) => d, 'desc'))[0];
									//   return formatYAxis(newValue * (percentage / 100), opts);
									// }
									return formatYAxis(v, opts);
								},
							},
						},
						xaxis: {
							labels: {
								formatter: (v: any) => {
									return v
										? formatByLocale(
												v,
												seriesType === "day"
													? `MMM'\n'do`
													: seriesType === "week"
														? `MMM'\nWeek 'I`
														: seriesType === "month"
															? `MMM`
															: null,
												{
													weekStartsOn: 1,
													locale: this.userSettingsQuery.getValue().settings.languageSetting,
												}
											).split("\n")
										: "-";
								},
							},
						},
					});
				}
			)
		)
	);
	get viewRange() {
		const { from, to } = this.range;
		return new DateRange(from, to);
	}
	readonly viewDate$status = this.range$.pipe(
		map(({ from: start, to: end }) => {
			const sameDay = isSameDay(start, end);
			return {
				start,
				end,
				isToday: isSameDay(start, end) && isSameDay(start, new Date()),
				sameDay,
			};
		})
	);
	readonly viewDate$loading = this.reports.timeLoading$;
}
